import { inject, Injectable } from '@angular/core';
import {
  ForecastSeriesCompletedResponse,
  ForecastSeriesCompletedWithoutMeasure,
} from '@api-response-types/forecast-series.response';
import { TranslateService } from '@ngx-translate/core';
import { DataSeries } from 'demand-forecasting/types/data-series.interface';
import { ChartColorService } from 'demand-forecasting/services/chart/chart-color.service';

/**
 * TODO: remove this service
 * general note: the service is used to convert the response from the backend to the format that is used in the frontend
 * in future we should refactor this so that the whole service is not needed anymore
 * we want to use data directly from the store without any conversion
 * the reason this service is still needed is that we can not run service function inside the store selectors
 */
@Injectable({
  providedIn: 'root',
})
export class ForecastSeriesService {
  private readonly translateService = inject(TranslateService);
  private readonly colorService = inject(ChartColorService);

  private readonly forecastName: string;
  private readonly forecastColor: string[];

  constructor() {
    this.forecastColor = this.colorService.getColors('aggregated');
    this.forecastName = this.translateService.instant(
      'RESULT.FILTER.AGGREGATE',
    );
  }

  public toSeriesData(
    forecastSeriesCompletedResponse: ForecastSeriesCompletedWithoutMeasure,
  ): DataSeries {
    return {
      additionalTimeSeries:
        forecastSeriesCompletedResponse.additionalTimeSeries,
      name: this.forecastName || forecastSeriesCompletedResponse.name,
      colors: this.forecastColor,
      values: this.generateForecastSeriesValues(
        forecastSeriesCompletedResponse.values,
      ),
      predictions: this.generateForecastSeriesPredictions(
        forecastSeriesCompletedResponse.predictions,
      ),
      confidences: this.generateForecastSeriesConfidences(
        forecastSeriesCompletedResponse.confidences,
      ),
      manualPredictionsForChart:
        this.generateForecastSeriesManualPredictionsForChart(
          forecastSeriesCompletedResponse.manualPredictionsForChart,
        ),
      manualPredictions: forecastSeriesCompletedResponse.manualPredictions,
      manualPredictionComments:
        forecastSeriesCompletedResponse.manualPredictionComments,
      explanations: forecastSeriesCompletedResponse.explanations ?? [],
      originalDates: forecastSeriesCompletedResponse.originalDates,
      visible: true,
      initialForecastIndex:
        forecastSeriesCompletedResponse.initialForecastIndex,
      xAxis: forecastSeriesCompletedResponse.xAxis,
      yAxis: forecastSeriesCompletedResponse.yAxis,
      featureImportance: forecastSeriesCompletedResponse.featureImportance,
      configurationId: forecastSeriesCompletedResponse.configurationId,
      datasourceInfo: forecastSeriesCompletedResponse.datasourceInfo,
      resultSetId: forecastSeriesCompletedResponse.resultSetId,
    };
  }

  private generateForecastSeriesValues(
    values: ForecastSeriesCompletedResponse['values'],
  ): DataSeries['values'] {
    return {
      data: values.data,
      incompleteTruth: values.incompleteTruth,
      visible: true,
      name: this.translateService.instant('RESULT.CHART.REAL'),
    };
  }

  private generateForecastSeriesPredictions(
    predictions: ForecastSeriesCompletedResponse['predictions'],
  ): DataSeries['predictions'] {
    return {
      data: predictions.data,
      visible: true,
      name: this.translateService.instant('RESULT.CHART.PREDICTION'),
    };
  }

  private generateForecastSeriesConfidences(
    confidences: ForecastSeriesCompletedResponse['confidences'],
  ): DataSeries['confidences'] {
    return {
      type: 'arearange',
      data: confidences.data,
      visible: true,
      name: this.translateService.instant('RESULT.CHART.CONFIDENCE_INTERVAL'),
    };
  }

  private generateForecastSeriesManualPredictionsForChart(
    manualPredictionsForChart: ForecastSeriesCompletedResponse['manualPredictionsForChart'],
  ): DataSeries['manualPredictionsForChart'] {
    return {
      data: manualPredictionsForChart.data,
      visible: true,
      name: this.translateService.instant('RESULT.CHART.MANUAL_PREDICTION'),
    };
  }
}
